import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api';

export const useDashboard = () => {
  // Core state
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progressData, setProgressData] = useState({ consumed: [], remaining: [] });
  const [userTargets, setUserTargets] = useState(null);  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  });
  const [foodLog, setFoodLog] = useState([]);
  const [macroResult, setMacroResult] = useState(null);
  const [scannedBarcode, setScannedBarcode] = useState(null);
  const [manualEntry, setManualEntry] = useState(null);  const [editMealId, setEditMealId] = useState(null);
  const [editMealData, setEditMealData] = useState({});

  // Modal state
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);

  // Scan feedback state
  const [scanLoading, setScanLoading] = useState(false);  const [scanError, setScanError] = useState('');
  const [scannedProductData, setScannedProductData] = useState(null);
  const [showScannedProduct, setShowScannedProduct] = useState(false);
  
  const location = useLocation();
  const scanHandledRef = useRef(false);

  // Fetch user targets on component mount
  useEffect(() => {
    const fetchUserTargets = async () => {
      try {
        const response = await api.get('/auth/targets');
        setUserTargets(response.data.targets);
      } catch (err) {
        console.error('Error fetching user targets:', err);
        // Keep userTargets as null to use defaults
      }
    };
    fetchUserTargets();
  }, []);
  // Reset to today when navigating to dashboard
  useEffect(() => {
    const today = new Date();
    const todayIso = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
    setSelectedDate(todayIso);
  }, [location.pathname]);

  // Fetch food log when date changes
  useEffect(() => {
    const fetchFoodLog = async () => {
      try {
        const foodLogRes = await api.get(`/meals?date=${selectedDate}`);
        setFoodLog(foodLogRes.data);
      } catch (err) {
        setFoodLog([]);
      }
    };
    fetchFoodLog();
  }, [selectedDate]);

  // Helper functions
  const getMacroSummary = () => {
    let foodEntries = foodLog.food || foodLog;
    let exerciseEntries = foodLog.exercise || [];
    let totalCalories = 0, totalProtein = 0, totalCarbs = 0, totalFat = 0, totalExercise = 0;
    
    for (const item of foodEntries) {
      totalCalories += Number(item.calories) || 0;
      totalProtein += Number(item.protein) || 0;
      totalCarbs += Number(item.carbs) || 0;
      totalFat += Number(item.fat) || 0;
    }
    
    for (const ex of exerciseEntries) {
      totalExercise += Number(ex.calories) || 0;
    }
    
    return {
      calories: totalCalories,
      protein: totalProtein,
      carbs: totalCarbs,
      fat: totalFat,
      exercise: totalExercise,
    };
  };
  const getTargets = () => {
    // First priority: user-specific targets from profile
    if (userTargets) {
      return userTargets;
    }
    
    // Second priority: targets from progress data
    if (progressData && progressData.targets) {
      return progressData.targets;
    }
    
    // Fallback to default values
    return {
      calories: 2000,
      protein: 100,
      carbs: 130,
      fat: 50,
    };
  };

  const refreshDashboardData = async () => {
    try {
      const progressRes = await api.get(`/progress/daily?date=${selectedDate}`);
      const { protein, carbs, fats, targetProtein, targetCarbs, targetFats } = progressRes.data.progress;
      setProgressData({
        consumed: [protein, carbs, fats],
        remaining: [
          targetProtein - protein,
          targetCarbs - carbs,
          targetFats - fats,
        ],
      });
    } catch (err) {
      console.error('Error fetching progress data:', err);
    }

    try {
      const foodLogRes = await api.get(`/meals?date=${selectedDate}`);
      setFoodLog(foodLogRes.data);
    } catch (err) {
      setFoodLog([]);
    }
  };

  // Event handlers
  const handlePromptSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const now = new Date();
      const res = await api.post('/gemini/macro', { prompt, date: now.toISOString() });
      setMacroResult(res.data.result);
      setPrompt('');
      await refreshDashboardData();
    } catch (err) {
      setError('Failed to fetch macro data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const name = form.name.value;
    const calories = Number(form.calories.value);
    const protein = Number(form.protein.value);
    const carbs = Number(form.carbs.value);
    const fat = Number(form.fat.value);
    const serving_size = form.serving_size?.value || '';
    const description = form.description?.value || '';
    
    try {
      const res = await fetch('/api/barcode/manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          barcode: manualEntry.barcode,
          name,
          calories,
          protein,
          carbs,
          fat,
          serving_size,
          description,
        }),
      });
      const data = await res.json();
      setManualEntry(null);
      if (data.result) {
        await refreshDashboardData();
      }
    } catch (err) {
      alert('Failed to save manual entry.');
    }
  };
  const handleMacroEdit = async (newMacros) => {
    try {
      // Update local state immediately for better UX
      setMacroResult((prev) => ({ ...prev, ...newMacros }));
      
      // Send update to backend
      await api.put(`/meals/${newMacros._id}`, {
        name: newMacros.name,
        calories: Number(newMacros.calories) || 0,
        protein: Number(newMacros.protein) || 0,
        carbs: Number(newMacros.carbs) || 0,
        fat: Number(newMacros.fat) || 0,
        serving_size: newMacros.serving_size || '',
        description: newMacros.description || '',
        date: selectedDate,
      });
      
      // Refresh data to ensure consistency
      await refreshDashboardData();
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Error updating macro data:', err);
      setError('Failed to update entry. Please try again.');
      // Revert local state on error
      await refreshDashboardData();
    }
  };  const handleEdit = async (item) => {
    try {
      // If this is just updating the prompt/name
      if (typeof item === 'string') {
        setPrompt(item);
        return;
      }
      
      // If this is updating a full meal entry with a new name
      if (item._id && item.name) {
        // Find the original entry to compare names
        const originalEntry = [...(foodLog.food || []), ...(foodLog.exercise || [])]
          .find(entry => entry._id === item._id);
        
        // Check if the name has changed significantly (more than just minor edits)
        const nameChanged = originalEntry && 
          originalEntry.name.trim().toLowerCase() !== item.name.trim().toLowerCase();
          if (nameChanged) {
          // Name changed significantly - trigger new AI analysis
          setLoading(true);
          setError('');
          
          try {            // Call Gemini API to get new nutritional analysis without creating a new entry
            const res = await api.post('/gemini/analyze', { 
              prompt: item.name
            });
            
            console.log('Gemini analyze response:', res.data);
            
            if (res.data.result) {
              // Update the existing entry with new AI-generated data
              const newMacroData = res.data.result;
              console.log('New macro data from AI:', newMacroData);
              
              const updateData = {
                name: newMacroData.name || item.name,
                calories: Number(newMacroData.calories) || 0,
                protein: Number(newMacroData.protein) || 0,
                carbs: Number(newMacroData.carbs) || 0,
                fat: Number(newMacroData.fat) || 0,
                serving_size: newMacroData.serving_size || '',
                description: newMacroData.description || '',
                date: selectedDate,
              };
              
              console.log('Update data being sent to API:', updateData);
              
              await api.put(`/meals/${item._id}`, updateData);
            } else {
              // Fallback to just updating the name if AI call fails
              await api.put(`/meals/${item._id}`, {
                name: item.name,
                calories: Number(item.calories) || 0,
                protein: Number(item.protein) || 0,
                carbs: Number(item.carbs) || 0,
                fat: Number(item.fat) || 0,
                serving_size: item.serving_size || '',
                description: item.description || '',
                date: selectedDate,
              });
            }
          } catch (geminiErr) {
            console.error('Error with AI analysis for edited prompt:', geminiErr);
            // Fallback to just updating the name if AI call fails
            await api.put(`/meals/${item._id}`, {
              name: item.name,
              calories: Number(item.calories) || 0,
              protein: Number(item.protein) || 0,
              carbs: Number(item.carbs) || 0,
              fat: Number(item.fat) || 0,
              serving_size: item.serving_size || '',
              description: item.description || '',
              date: selectedDate,
            });
          } finally {
            setLoading(false);
          }
        } else {
          // Name hasn't changed significantly - just update the existing data
          await api.put(`/meals/${item._id}`, {
            name: item.name,
            calories: Number(item.calories) || 0,
            protein: Number(item.protein) || 0,
            carbs: Number(item.carbs) || 0,
            fat: Number(item.fat) || 0,
            serving_size: item.serving_size || '',
            description: item.description || '',
            date: selectedDate,
          });
        }
        
        await refreshDashboardData();
        setError(''); // Clear any previous errors
      } else {
        // Fallback for simple name update
        setPrompt(item.name || item);
      }
    } catch (err) {
      console.error('Error updating entry:', err);
      setError('Failed to update entry. Please try again.');
    }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }
    
    try {
      await api.delete(`/meals/${id}`);
      await refreshDashboardData();
      // Clear any error states on successful deletion
      setError('');
    } catch (err) {
      setError('Failed to delete entry. Please try again.');
      console.error('Delete error:', err);
    }
  };

  const handleEditChange = (e) => {
    setEditMealData({ ...editMealData, [e.target.name]: e.target.value });
  };

  const handleEditSave = async () => {
    await fetch(`/api/barcode/manual`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        barcode: editMealData.barcode,
        name: editMealData.name,
        calories: Number(editMealData.calories),
        protein: Number(editMealData.protein),
        carbs: Number(editMealData.carbs),
        fat: Number(editMealData.fat),
        serving_size: editMealData.serving_size,
        description: editMealData.description,
        date: editMealData.date,
      }),
    });
    setEditMealId(null);
    await refreshDashboardData();
  };

  const handleEditCancel = () => {
    setEditMealId(null);
  };
  const handleBarcodeDetected = async (barcode) => {
    if (scanHandledRef.current) return;
    scanHandledRef.current = true;
    
    if (!barcode) return;
    setScannedBarcode(barcode);
    setScanLoading(true);
    setScanError('');
    setLoading(true);
    setError('');
    
    try {
      const res = await api.post('/gemini/barcode', { barcode, date: selectedDate });
      if (res.data.requireManualEntry) {
        setManualEntry({ barcode: res.data.barcode });
        setScanError('Product not found. Please enter nutrition info manually.');
        setShowScannedProduct(false);
      } else if (res.data.result) {
        const now = new Date();
        const resultWithTime = {
          ...res.data.result,
          date: now.toISOString(),
        };
        
        // Store the scanned product data to display in modal
        setScannedProductData(resultWithTime);
        setShowScannedProduct(true);
        
        // Don't close the modal yet - let user confirm first
      }
    } catch (err) {
      setScanError('Failed to scan barcode. Please try again.');
      setShowScannedProduct(false);
    } finally {
      setScanLoading(false);
      setLoading(false);
      setTimeout(() => { scanHandledRef.current = false; }, 1000);
    }
  };

  const handleConfirmScannedProduct = async () => {
    if (scannedProductData) {
      setMacroResult({ ...scannedProductData, _source: scannedProductData.source });
      await refreshDashboardData();
    }
    setShowScannedProduct(false);
    setScannedProductData(null);
    setShowBarcodeModal(false);
  };

  const handleRetryBarcodeScan = () => {
    setShowScannedProduct(false);
    setScannedProductData(null);
    setScanError('');
    // Keep the modal open for another scan
  };
  return {
    // State
    prompt,
    setPrompt,
    loading,
    error,
    setError,
    progressData,
    userTargets,
    setUserTargets,
    selectedDate,
    setSelectedDate,
    foodLog,
    macroResult,
    setMacroResult,
    scannedBarcode,
    manualEntry,
    setManualEntry,
    editMealId,
    setEditMealId,
    editMealData,    setEditMealData,    scanLoading,    scanError,
    scannedProductData,
    showScannedProduct,
    setShowScannedProduct,
    scanHandledRef,
    showBarcodeModal,
    setShowBarcodeModal,
    
    // Helper functions
    getMacroSummary,
    getTargets,
    refreshDashboardData,
    
    // Event handlers
    handlePromptSubmit,
    handleManualSubmit,
    handleMacroEdit,
    handleEdit,
    handleDelete,
    handleEditChange,
    handleEditSave,    handleEditCancel,
    handleBarcodeDetected,
    handleConfirmScannedProduct,
    handleRetryBarcodeScan,
  };
};
